import React, { useEffect, useState, Component } from 'react';
import { LayersIcon, ZoomInIcon, ZoomOutIcon, HomeIcon, ServerIcon, DatabaseIcon, WifiIcon, ShieldIcon, CloudIcon, CodeIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icons in Leaflet with webpack
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});
interface MapProps {
  activeFilter: string;
  activeCategory: string;
  activeStatus: string;
  activeClassification: string;
  dateRange: {
    start: string;
    end: string;
  };
  onAssetSelect: (asset: any) => void;
}
// Component to handle map view changes
function SetViewOnChange({
  center,
  zoom
}: {
  center: [number, number];
  zoom: number;
}) {
  const map = useMap();
  map.setView(center, zoom);
  return null;
}
export const Map: React.FC<MapProps> = ({
  activeFilter,
  activeCategory,
  activeStatus,
  activeClassification,
  dateRange,
  onAssetSelect
}) => {
  const [loading, setLoading] = useState(true);
  const [mapMode, setMapMode] = useState('marker'); // 'marker', 'cluster', 'heatmap'
  const [zoom, setZoom] = useState(5); // Zoom level for Indonesia
  const [showBoundaries, setShowBoundaries] = useState(true);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-2.5489, 118.0149]); // Center of Indonesia
  // Sample assets data with real geo coordinates for Indonesia
  const assets = [{
    id: 'AST001',
    name: 'Server Database Utama',
    category: 'server',
    type: 'server',
    location: 'pusat',
    region: 'Jakarta',
    position: {
      lat: -6.1751,
      lng: 106.865
    },
    status: 'active',
    classification: 'sangat-rahasia',
    expiry: '2026-12-30',
    value: 'Rp 450M',
    pic: 'Budi Santoso',
    picContact: '081234567890',
    vendor: 'PT Teknologi Maju',
    vendorContact: '021-7654321',
    lastUpdate: '2026-01-15'
  }, {
    id: 'AST002',
    name: 'Router Backbone STIN',
    category: 'network',
    type: 'router',
    location: 'stin',
    region: 'Bogor',
    position: {
      lat: -6.6,
      lng: 106.8
    },
    status: 'maintenance',
    classification: 'rahasia',
    expiry: '2026-06-15',
    value: 'Rp 120M',
    pic: 'Ani Wijaya',
    picContact: '081234567891',
    vendor: 'PT Jaringan Cepat',
    vendorContact: '021-7654322',
    lastUpdate: '2026-01-22'
  }, {
    id: 'AST003',
    name: 'Lisensi Oracle BINDA Jatim',
    category: 'software',
    type: 'database',
    location: 'binda',
    region: 'Jawa Timur',
    position: {
      lat: -7.536,
      lng: 112.2384
    },
    status: 'expiring',
    classification: 'rahasia',
    expiry: '2026-03-05',
    value: 'Rp 85M',
    pic: 'Candra Irawan',
    picContact: '081234567892',
    vendor: 'PT Oracle Indonesia',
    vendorContact: '021-7654323',
    lastUpdate: '2026-01-18'
  }, {
    id: 'AST004',
    name: 'Firewall Pusat Data',
    category: 'security',
    type: 'firewall',
    location: 'binda',
    region: 'Sumatera Barat',
    position: {
      lat: -0.95,
      lng: 100.353
    },
    status: 'critical',
    classification: 'sangat-rahasia',
    expiry: '2026-02-10',
    value: 'Rp 75M',
    pic: 'Deni Pratama',
    picContact: '081234567893',
    vendor: 'PT Keamanan Siber',
    vendorContact: '021-7654324',
    lastUpdate: '2026-01-05'
  }, {
    id: 'AST005',
    name: 'Platform Virtualisasi VMware',
    category: 'digital',
    type: 'virtualization',
    location: 'deputi',
    region: 'Jakarta',
    position: {
      lat: -6.185,
      lng: 106.845
    },
    status: 'active',
    classification: 'rahasia',
    expiry: '2026-04-18',
    value: 'Rp 350M',
    pic: 'Eko Sutrisno',
    picContact: '081234567894',
    vendor: 'PT Solusi Digital',
    vendorContact: '021-7654325',
    lastUpdate: '2026-01-18'
  }, {
    id: 'AST006',
    name: 'Perangkat Enkripsi Jaringan',
    category: 'security',
    type: 'encryption',
    location: 'binda',
    region: 'Jawa Timur',
    position: {
      lat: -7.6,
      lng: 112.5
    },
    status: 'maintenance',
    classification: 'sangat-rahasia',
    expiry: '2026-02-20',
    value: 'Rp 95M',
    pic: 'Fani Gunawan',
    picContact: '081234567895',
    vendor: 'PT Keamanan Digital',
    vendorContact: '021-7654326',
    lastUpdate: '2026-01-12'
  }, {
    id: 'AST007',
    name: 'Server CCTV BINDA Sulsel',
    category: 'server',
    type: 'server',
    location: 'binda',
    region: 'Sulawesi Selatan',
    position: {
      lat: -5.1477,
      lng: 119.4327
    },
    status: 'active',
    classification: 'rahasia',
    expiry: '2026-03-15',
    value: 'Rp 65M',
    pic: 'Gunawan Wibowo',
    picContact: '081234567896',
    vendor: 'PT Teknologi Maju',
    vendorContact: '021-7654327',
    lastUpdate: '2026-01-30'
  }, {
    id: 'AST008',
    name: 'Switch Core Cisco Nexus',
    category: 'network',
    type: 'switch',
    location: 'satker',
    region: 'Jawa Barat',
    position: {
      lat: -6.9147,
      lng: 107.6098
    },
    status: 'active',
    classification: 'rahasia',
    expiry: '2026-05-10',
    value: 'Rp 55M',
    pic: 'Hendra Kusuma',
    picContact: '081234567897',
    vendor: 'PT Komunikasi Handal',
    vendorContact: '021-7654328',
    lastUpdate: '2026-01-25'
  }, {
    id: 'AST009',
    name: 'Sistem Backup & Recovery',
    category: 'digital',
    type: 'backup',
    location: 'binda',
    region: 'Papua',
    position: {
      lat: -4.2699,
      lng: 138.0804
    },
    status: 'validation',
    classification: 'terbuka',
    expiry: '2026-08-20',
    value: 'Rp 110M',
    pic: 'Indra Jaya',
    picContact: '081234567898',
    vendor: 'PT Data Aman',
    vendorContact: '021-7654329',
    lastUpdate: '2026-01-10'
  }, {
    id: 'AST010',
    name: 'Lisensi Analitik Big Data',
    category: 'software',
    type: 'analytics',
    location: 'pusat',
    region: 'Jakarta',
    position: {
      lat: -6.19,
      lng: 106.85
    },
    status: 'expiring',
    classification: 'sangat-rahasia',
    expiry: '2026-02-28',
    value: 'Rp 220M',
    pic: 'Kurniawan Hadi',
    picContact: '081234567899',
    vendor: 'PT Software Analytics',
    vendorContact: '021-7654330',
    lastUpdate: '2026-01-15'
  }];
  // BINDA Region boundaries with actual geo coordinates
  const bindaBoundaries = [{
    name: 'BINDA Jawa Timur',
    center: [-7.536, 112.2384],
    radius: 100000,
    color: 'rgba(59, 130, 246, 0.2)',
    stroke: 'rgba(59, 130, 246, 0.5)'
  }, {
    name: 'BINDA Sumatera Barat',
    center: [-0.95, 100.353],
    radius: 100000,
    color: 'rgba(245, 158, 11, 0.2)',
    stroke: 'rgba(245, 158, 11, 0.5)'
  }, {
    name: 'BINDA Sulawesi Selatan',
    center: [-5.1477, 119.4327],
    radius: 100000,
    color: 'rgba(16, 185, 129, 0.2)',
    stroke: 'rgba(16, 185, 129, 0.5)'
  }, {
    name: 'BINDA Papua',
    center: [-4.2699, 138.0804],
    radius: 100000,
    color: 'rgba(139, 92, 246, 0.2)',
    stroke: 'rgba(139, 92, 246, 0.5)'
  }];
  // Filter assets based on active filters
  const filteredAssets = assets.filter(asset => {
    const matchesFilter = activeFilter === 'all' || asset.location === activeFilter;
    const matchesCategory = activeCategory === 'all' || asset.category === activeCategory;
    const matchesStatus = activeStatus === 'all' || asset.status === activeStatus;
    const matchesClassification = activeClassification === 'all' || asset.classification === activeClassification;
    // Date range filter
    let matchesDateRange = true;
    if (dateRange.start && dateRange.end) {
      const assetDate = new Date(asset.expiry);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      matchesDateRange = assetDate >= startDate && assetDate <= endDate;
    }
    return matchesFilter && matchesCategory && matchesStatus && matchesClassification && matchesDateRange;
  });
  // Simulate map loading
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [activeFilter, activeCategory, activeStatus, activeClassification, dateRange]);
  // Get marker icon based on asset category
  const getMarkerIcon = asset => {
    let iconHtml = '';
    switch (asset.category) {
      case 'server':
        iconHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect><rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect><line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line></svg>';
        break;
      case 'software':
        iconHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>';
        break;
      case 'network':
        iconHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"></path><path d="M1.42 9a16 16 0 0 1 21.16 0"></path><path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path><line x1="12" y1="20" x2="12.01" y2="20"></line></svg>';
        break;
      case 'security':
        iconHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>';
        break;
      case 'digital':
        iconHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path></svg>';
        break;
      default:
        iconHtml = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect><rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect><line x1="6" y1="6" x2="6.01" y2="6"></line><line x1="6" y1="18" x2="6.01" y2="18"></line></svg>';
    }
    // Get color based on status
    const bgColor = getMarkerColor(asset.status);
    return L.divIcon({
      html: `<div class="marker-icon ${bgColor}">${iconHtml}</div>`,
      className: 'custom-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  };
  // Get marker color based on asset status
  const getMarkerColor = status => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'maintenance':
        return 'bg-yellow-500';
      case 'expiring':
        return 'bg-orange-500';
      case 'critical':
        return 'bg-red-500';
      case 'validation':
        return 'bg-purple-500';
      default:
        return 'bg-blue-500';
    }
  };
  // Handle zoom in
  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 1, 8));
  };
  // Handle zoom out
  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 1, 4));
  };
  // Handle reset view
  const handleResetView = () => {
    setZoom(5);
    setMapCenter([-2.5489, 118.0149]); // Center of Indonesia
  };
  // Generate cluster markers
  const generateClusterMarkers = () => {
    if (mapMode !== 'cluster') return null;
    const clusters = [{
      position: [-6.1751, 106.865],
      count: 12,
      color: 'bg-primary'
    }, {
      position: [-7.536, 112.2384],
      count: 8,
      color: 'bg-accent-info'
    }, {
      position: [-0.95, 100.353],
      count: 5,
      color: 'bg-yellow-500'
    }, {
      position: [-5.1477, 119.4327],
      count: 3,
      color: 'bg-accent-red'
    }, {
      position: [-4.2699, 138.0804],
      count: 2,
      color: 'bg-purple-500'
    }];
    return clusters.map((cluster, index) => <Marker key={`cluster-${index}`} position={cluster.position} icon={L.divIcon({
      html: `<div class="${cluster.color} w-8 h-8 rounded-full flex items-center justify-center shadow-lg text-white font-bold">${cluster.count}</div>`,
      className: 'custom-cluster-marker',
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    })} eventHandlers={{
      click: () => alert(`Cluster dengan ${cluster.count} aset`)
    }} data-id="element-603" />);
  };
  // Generate heatmap overlay
  const generateHeatmap = () => {
    if (mapMode !== 'heatmap') return null;
    // For heatmap, we'll use Circle components with different radiuses and opacities
    const heatPoints = [{
      center: [-6.1751, 106.865],
      radius: 50000,
      color: 'red',
      opacity: 0.5
    }, {
      center: [-7.536, 112.2384],
      radius: 70000,
      color: 'red',
      opacity: 0.4
    }, {
      center: [-0.95, 100.353],
      radius: 60000,
      color: 'red',
      opacity: 0.45
    }, {
      center: [-6.1751, 106.965],
      radius: 80000,
      color: 'yellow',
      opacity: 0.3
    }, {
      center: [-7.28, 112.75],
      radius: 40000,
      color: 'yellow',
      opacity: 0.35
    }, {
      center: [-2.99, 104.756],
      radius: 90000,
      color: 'green',
      opacity: 0.2
    }, {
      center: [-5.1477, 119.4327],
      radius: 50000,
      color: 'green',
      opacity: 0.25
    } // Makassar - normal
    ];
    return heatPoints.map((point, index) => <Circle key={`heat-${index}`} center={point.center} radius={point.radius} pathOptions={{
      fillColor: point.color,
      fillOpacity: point.opacity,
      stroke: false
    }} data-id="element-604" />);
  };
  return <div className="relative h-full w-full bg-neutral-light" data-id="element-605">
      {loading ? <div className="absolute inset-0 flex items-center justify-center" data-id="element-606">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" data-id="element-607"></div>
        </div> : <div className="h-full w-full relative" data-id="element-608">
          <div className="absolute inset-0" data-id="element-609">
            <MapContainer center={mapCenter} zoom={zoom} style={{
          height: '100%',
          width: '100%'
        }} zoomControl={false} data-id="element-610">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-611" />
              <SetViewOnChange center={mapCenter} zoom={zoom} data-id="element-612" />
              {/* Display region boundaries */}
              {showBoundaries && bindaBoundaries.map((region, index) => <Circle key={`region-${index}`} center={region.center} radius={region.radius} pathOptions={{
            fillColor: region.color,
            fillOpacity: 0.2,
            color: region.stroke,
            weight: 2,
            dashArray: '4 2'
          }} eventHandlers={{
            click: () => onAssetSelect({
              name: region.name,
              type: 'region'
            })
          }} data-id="element-613" />)}
              {/* STIN Special Marker */}
              <Circle center={[-6.6, 106.8]} // STIN Bogor
          radius={10000} pathOptions={{
            fillColor: 'rgba(59, 130, 246, 0.3)',
            color: '#3B82F6',
            weight: 2,
            dashArray: '2 1'
          }} eventHandlers={{
            click: () => onAssetSelect({
              name: 'STIN - Sekolah Tinggi Intelijen Negara',
              type: 'poi',
              location: 'stin'
            })
          }} data-id="element-614" />
              {/* Display markers based on map mode */}
              {mapMode === 'marker' && filteredAssets.map(asset => <Marker key={asset.id} position={[asset.position.lat, asset.position.lng]} icon={getMarkerIcon(asset)} eventHandlers={{
            click: () => onAssetSelect(asset)
          }} data-id="element-615">
                    <Popup data-id="element-616">
                      <div className="text-center" data-id="element-617">
                        <h3 className="font-medium text-secondary" data-id="element-618">
                          {asset.name}
                        </h3>
                        <p className="text-xs text-gray-500" data-id="element-619">{asset.region}</p>
                      </div>
                    </Popup>
                  </Marker>)}
              {/* Display cluster markers */}
              {generateClusterMarkers()}
              {/* Display heatmap */}
              {generateHeatmap()}
            </MapContainer>
          </div>
          {/* Map controls */}
          <div className="absolute top-4 left-4 bg-white bg-opacity-90 p-2 rounded-lg border border-gray-300 shadow-md flex flex-col space-y-2 z-[1000]" data-id="element-620">
            <button className={`p-2 rounded-md ${zoom >= 8 ? 'bg-gray-200' : 'hover:bg-gray-100'}`} onClick={handleZoomIn} disabled={zoom >= 8} data-id="element-621">
              <ZoomInIcon size={16} className="text-secondary" data-id="element-622" />
            </button>
            <button className={`p-2 rounded-md ${zoom <= 4 ? 'bg-gray-200' : 'hover:bg-gray-100'}`} onClick={handleZoomOut} disabled={zoom <= 4} data-id="element-623">
              <ZoomOutIcon size={16} className="text-secondary" data-id="element-624" />
            </button>
            <button className="p-2 rounded-md hover:bg-gray-100" onClick={handleResetView} data-id="element-625">
              <HomeIcon size={16} className="text-secondary" data-id="element-626" />
            </button>
            <div className="h-px bg-gray-300 w-full" data-id="element-627"></div>
            <button className={`p-2 rounded-md ${mapMode === 'marker' ? 'bg-primary text-white' : 'hover:bg-gray-100'}`} onClick={() => setMapMode('marker')} title="Individual Markers" data-id="element-628">
              <ServerIcon size={16} data-id="element-629" />
            </button>
            <button className={`p-2 rounded-md ${mapMode === 'cluster' ? 'bg-primary text-white' : 'hover:bg-gray-100'}`} onClick={() => setMapMode('cluster')} title="Cluster View" data-id="element-630">
              <LayersIcon size={16} data-id="element-631" />
            </button>
            <button className={`p-2 rounded-md ${mapMode === 'heatmap' ? 'bg-primary text-white' : 'hover:bg-gray-100'}`} onClick={() => setMapMode('heatmap')} title="Heatmap View" data-id="element-632">
              <div className="w-4 h-4 bg-gradient-to-tr from-green-500 via-yellow-500 to-red-500 rounded-sm opacity-80" data-id="element-633"></div>
            </button>
            <div className="h-px bg-gray-300 w-full" data-id="element-634"></div>
            <button className={`p-2 rounded-md ${showBoundaries ? 'bg-primary text-white' : 'hover:bg-gray-100'}`} onClick={() => setShowBoundaries(!showBoundaries)} title="Toggle Boundaries" data-id="element-635">
              <div className="w-4 h-4 border-2 border-current rounded-sm border-dashed" data-id="element-636"></div>
            </button>
          </div>
          {/* Legend */}
          <div className="absolute bottom-4 right-4 bg-white bg-opacity-90 p-3 rounded-lg border border-gray-300 shadow-md z-[1000]" data-id="element-637">
            <div className="text-xs font-bold mb-2 text-secondary" data-id="element-638">Legenda</div>
            <div className="flex items-center space-x-2 text-xs mb-1.5" data-id="element-639">
              <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-640"></div>
              <span className="text-secondary" data-id="element-641">Aset Aktif</span>
            </div>
            <div className="flex items-center space-x-2 text-xs mb-1.5" data-id="element-642">
              <div className="w-3 h-3 bg-yellow-500 rounded-full" data-id="element-643"></div>
              <span className="text-secondary" data-id="element-644">Perlu Perawatan</span>
            </div>
            <div className="flex items-center space-x-2 text-xs mb-1.5" data-id="element-645">
              <div className="w-3 h-3 bg-orange-500 rounded-full" data-id="element-646"></div>
              <span className="text-secondary" data-id="element-647">Akan Habis</span>
            </div>
            <div className="flex items-center space-x-2 text-xs mb-1.5" data-id="element-648">
              <div className="w-3 h-3 bg-red-500 rounded-full" data-id="element-649"></div>
              <span className="text-secondary" data-id="element-650">Kritis</span>
            </div>
            <div className="flex items-center space-x-2 text-xs mb-1.5" data-id="element-651">
              <div className="w-3 h-3 bg-purple-500 rounded-full" data-id="element-652"></div>
              <span className="text-secondary" data-id="element-653">Perlu Validasi</span>
            </div>
            <div className="h-px bg-gray-300 w-full my-1.5" data-id="element-654"></div>
            <div className="flex items-center space-x-2 text-xs mb-1.5" data-id="element-655">
              <div className="w-3 h-3 border-2 border-blue-500 rounded-full border-dashed" data-id="element-656"></div>
              <span className="text-secondary" data-id="element-657">Boundary BINDA</span>
            </div>
            <div className="flex items-center space-x-2 text-xs mb-1.5" data-id="element-658">
              <div className="w-3 h-3 bg-blue-500/30 rounded-full border border-blue-500" data-id="element-659"></div>
              <span className="text-secondary" data-id="element-660">POI STIN</span>
            </div>
          </div>
          {/* Filter indicator */}
          <div className="absolute top-4 right-4 bg-white bg-opacity-90 p-2 rounded-lg border border-gray-300 shadow-md max-w-xs z-[1000]" data-id="element-661">
            <div className="text-xs text-secondary" data-id="element-662">
              <span className="font-bold" data-id="element-663">Lokasi: </span>
              <span data-id="element-664">
                {activeFilter === 'all' ? 'Semua Lokasi' : activeFilter === 'pusat' ? 'Pusat/Setuma' : activeFilter === 'deputi' ? 'Deputi' : activeFilter === 'stin' ? 'STIN' : activeFilter === 'binda' ? 'BINDA' : 'Satker'}
              </span>
              {activeCategory !== 'all' && <span className="ml-1" data-id="element-665">
                  |<span className="ml-1 font-bold" data-id="element-666">Kategori: </span>
                  {activeCategory === 'server' ? 'Server & Komputasi' : activeCategory === 'software' ? 'Software & Lisensi' : activeCategory === 'network' ? 'Jaringan & Komunikasi' : activeCategory === 'security' ? 'Keamanan Siber' : 'Infrastruktur Digital'}
                </span>}
              {activeStatus !== 'all' && <span className="ml-1" data-id="element-667">
                  |<span className="ml-1 font-bold" data-id="element-668">Status: </span>
                  {activeStatus === 'active' ? 'Aktif' : activeStatus === 'maintenance' ? 'Perlu Perawatan' : activeStatus === 'expiring' ? 'Akan Habis' : activeStatus === 'critical' ? 'Kritis' : 'Perlu Validasi'}
                </span>}
            </div>
          </div>
          {/* CSS for custom markers */}
          <style jsx data-id="element-669">{`
            .custom-marker {
              background: transparent;
            }
            .marker-icon {
              width: 30px;
              height: 30px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
              position: relative;
            }
            .marker-icon:after {
              content: '';
              position: absolute;
              bottom: -8px;
              left: 10px;
              border-width: 8px 5px 0;
              border-style: solid;
              border-color: currentColor transparent transparent;
            }
          `}</style>
        </div>}
    </div>;
};